import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import DatePicker from 'react-datepicker';

import {
  getValidationResults,
  searchValidationResults,
  exportToExcel,
  getFormPdf,
  getAllocationStatementFile,
  hideOwnershipModal,
  takeQueueItem
} from 'actions';

import { history } from 'containers/App';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';

import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';

import ReactTable from 'components/ReactTable';
import RejectedFormsColDefs from 'components/ValidationResults/GridColumnDefinitions/rejectedFormColumnDefs';
import ValidFormsColDefs from 'components/ValidationResults/GridColumnDefinitions/validFormColumnDefs';
import ExpiredFormsColDefs from 'components/ValidationResults/GridColumnDefinitions/expiredFormColumnDefs';
import FormsActions from 'components/ValidationResults/GridActions/FormActions';
import ChangeOwnershipModal from 'components/Modals/ChangeOwnershipModal';

const WAIT_INTERVAL = 1000;
const ENTER_KEY = 13;

class ValidationResults extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    settings: PropTypes.shape(),
    gridTotalLimit: PropTypes.number.isRequired,
    // isGettingReporting: PropTypes.bool.isRequired,
    reportingResponse: PropTypes.shape({
      results: PropTypes.arrayOf(PropTypes.shape()),
      totalCount: PropTypes.number
    }),
    isExportingToExcel: PropTypes.bool.isRequired,
    excelData: PropTypes.shape({}),
    isOwnershipModalShown: PropTypes.bool,
    changeOwnershipItem: PropTypes.shape(),
    initSearchCriteria: PropTypes.shape(),
    initGridCriteria: PropTypes.shape(),
    dateFormat: PropTypes.string,
    piiValidationCodes: PropTypes.shape(),
  };

  static defaultProps = {
    permissions: null,
    settings: null,
    reportingResponse: null,
    excelData: null,
    isOwnershipModalShown: false,
    changeOwnershipItem: null,
    initSearchCriteria: null,
    initGridCriteria: null,
    dateFormat: 'MMM dd yyyy',
    piiValidationCodes: null,
  };

  constructor(props) {
    super(props);

    const { initGridCriteria, initSearchCriteria, permissions, settings, piiValidationCodes, gridTotalLimit } = props;
    const searchCriteria = initSearchCriteria || {
      searchQuery: '',
      dateFrom: null,
      dateTo: null,
    };
    const gridCriteria = initGridCriteria || {
      columnDefs: RejectedFormsColDefs(permissions, settings.selectedClient.isPiiProtectionEnabled, piiValidationCodes).concat([]),
      pageIndex: 0,
      sortBy: [],
      filters: [],
      pageCount: 0,
      pageSize: 50,
      itemCount: 0,
      totalLimit: gridTotalLimit,
      selectedItems: [],
      selectedItem: null
    };
    this.state = {
      activeTab: 0,
      tabs: [
        {
          title: 'Invalid',
          index: 0,
          field: 'invalid',
          actions: FormsActions({
            permissions,
            settings,
            getAllocationStatementFileCallback: this.fetchAllocationStatementFile,
            getOriginalPdfFileCallback: this.fetchOriginalPdfFile
          }),
          colDefs: RejectedFormsColDefs(permissions, settings.selectedClient.isPiiProtectionEnabled, piiValidationCodes).concat([])
        },
        {
          title: 'Valid (With Expiry)',
          index: 1,
          field: 'validwithexpiry',

          actions: FormsActions({
            permissions,
            settings,
            getAllocationStatementFileCallback: this.fetchAllocationStatementFile,
            getOriginalPdfFileCallback: this.fetchOriginalPdfFile
          }),
          colDefs: ValidFormsColDefs(permissions, settings.selectedClient.isPiiProtectionEnabled, piiValidationCodes).concat([])
        },
        {
          title: 'Valid (No Expiry)',
          index: 2,
          field: 'validnoexpiry',
          actions: FormsActions({
            permissions,
            settings,
            getAllocationStatementFileCallback: this.fetchAllocationStatementFile,
            getOriginalPdfFileCallback: this.fetchOriginalPdfFile
          }),
          colDefs: ValidFormsColDefs(permissions, settings.selectedClient.isPiiProtectionEnabled, piiValidationCodes).concat([])
        },
        {
          title: 'Expired',
          index: 3,
          field: 'expired',
          actions: FormsActions({
            permissions,
            settings,
            getAllocationStatementFileCallback: this.fetchAllocationStatementFile,
            getOriginalPdfFileCallback: this.fetchOriginalPdfFile
          }),
          colDefs: ExpiredFormsColDefs(permissions, settings.selectedClient.isPiiProtectionEnabled, piiValidationCodes).concat([])
        }
      ],
      searchCriteria,
      gridCriteria,
      initialLoaded: false
    };
  }

  componentDidMount() {
    this.fetchReporting();
  }

  /* eslint-disable react/no-did-update-set-state */
  componentDidUpdate(prevProps) {
    const { reportingResponse } = this.props;
    const { initialLoaded, gridCriteria, tabs, activeTab } = this.state;

    const itemCount = reportingResponse.totalCount;
    const pageCount = Math.ceil(itemCount / gridCriteria.pageSize);
    const nextTabs = tabs != null && tabs.map((tab) => {
      if (tab != null) {
        if (!gridCriteria.searchQuery) {
          return {
            ...tab,
          };
        }
        if (tab.index === activeTab) {
          return {
            ...tab,
          };
        }
        return {
          ...tab,
        };
      }
      return null;
    });

    if (reportingResponse !== prevProps.reportingResponse) {
      if (!initialLoaded) {
        this.setState({ initialLoaded: true, loading: false, tabs: nextTabs, gridCriteria: { ...gridCriteria, itemCount, pageCount } });
      } else {
        this.setState({ loading: false, tabs: nextTabs, gridCriteria: { ...gridCriteria, itemCount, pageCount } });
      }
    }
  }
  /* eslint-enable react/no-did-update-set-state */

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(hideOwnershipModal());
  }

  getActions = () => {
    const { tabs, activeTab } = this.state;
    return tabs[activeTab].actions;
  };

  onGridCriteriaChanged = (newGridCriteria) => {
    this.setState({ gridCriteria: { ...newGridCriteria } }, () => this.fetchReporting());
  };

  fetchReporting = (filters) => {
    const { dispatch } = this.props;
    const { gridCriteria, searchCriteria: { searchQuery, dateFrom, dateTo }, activeTab } = this.state;

    // remove _1 suffix
    if (filters != null) {
      const keys = Object.keys(filters);
      for (let j = 0; j < keys.length; j += 1) {
        if (keys[j].endsWith('_1')) {
          filters[keys[j].substr(0, keys[j].length - 2)] = filters[keys[j]];
          delete filters[keys[j]];
        }
      }
    }

    const sortBy = gridCriteria.sortBy != null ? gridCriteria.sortBy[0]?.id : null;
    let sortDirection;
    if (sortBy != null) {
      sortDirection = gridCriteria.sortBy[0]?.desc ? 'desc' : 'asc';
    } else {
      sortDirection = null;
    }

    this.setState({ loading: true });

    if (!searchQuery && !dateFrom && !dateTo) {
      dispatch(getValidationResults(gridCriteria.pageIndex + 1, gridCriteria.pageSize, gridCriteria.totalLimit, sortBy, sortDirection, gridCriteria.filters, activeTab));
    } else {
      dispatch(searchValidationResults(
        gridCriteria.pageIndex + 1,
        gridCriteria.pageSize,
        gridCriteria.totalLimit,
        sortBy,
        sortDirection,
        gridCriteria.filters,
        searchQuery,
        dateFrom?.toLocaleString('en-us', { month: 'short', day: 'numeric', year: 'numeric' }).replace(/[^ -~]/g, ''),
        dateTo?.toLocaleString('en-us', { month: 'short', day: 'numeric', year: 'numeric' }).replace(/[^ -~]/g, ''),
        activeTab
      ));
    }
  };

  fetchAllocationStatementFile = (formId) => {
    const { dispatch } = this.props;
    dispatch(getAllocationStatementFile(formId));
  };

  fetchOriginalPdfFile = (formId) => {
    const { dispatch } = this.props;
    dispatch(getFormPdf({ id: formId, isAdditional: false }));
  };

  downloadExcel() {
    const { dispatch } = this.props;
    dispatch(exportToExcel());
  }

  selectTab(tabIndex) {
    const { tabs, gridCriteria } = this.state;
    gridCriteria.columnDefs = tabs[tabIndex].colDefs;
    gridCriteria.pageIndex = 0;
    this.setState({ activeTab: tabIndex, gridCriteria }, () => this.fetchReporting());
  }

  searchChanged(event) {
    clearTimeout(this.timer);
    const { searchCriteria } = this.state;
    const searchQuery = event.target.value;

    this.setState({ searchCriteria: { ...searchCriteria, searchQuery } });

    if (searchQuery.length > 0 && searchQuery.length < 2) {
      return;
    }

    this.timer = setTimeout(this.fetchReporting, WAIT_INTERVAL);
  }

  dateFromInGridEvent(event) {
    const { searchCriteria } = this.state;
    this.setState(
      { searchCriteria: { ...searchCriteria, dateFrom: event } },
      () => this.fetchReporting()
    );
  }

  dateToInGridEvent(event) {
    const { searchCriteria } = this.state;
    this.setState(
      { searchCriteria: { ...searchCriteria, dateTo: event } },
      () => this.fetchReporting()
    );
  }

  searchInGridEvent(event) {
    clearTimeout(this.timer);
    const { searchCriteria } = this.state;
    const searchQuery = event.target.value;

    this.setState({ searchCriteria: { ...searchCriteria, searchQuery } });

    if (searchQuery.length > 0 && searchQuery.length < 2) {
      return;
    }

    if (event.keyCode === ENTER_KEY) {
      this.fetchReporting();
    } else {
      this.timer = setTimeout(this.fetchReporting, WAIT_INTERVAL);
    }
  }

  reAssignItem() {
    const { dispatch, changeOwnershipItem } = this.props;

    if (changeOwnershipItem != null && changeOwnershipItem.id != null) {
      dispatch(takeQueueItem('form', changeOwnershipItem.id, changeOwnershipItem.accountId));
      dispatch(hideOwnershipModal());
    }
  }

  continueFromOwnershipModal() {
    const { dispatch, changeOwnershipItem } = this.props;
    dispatch(hideOwnershipModal());
    let redirectUrl = `/forms/${ changeOwnershipItem.formType.toLowerCase() }/${ changeOwnershipItem.id }`;
    if (changeOwnershipItem.accountId) {
      redirectUrl = `${ redirectUrl }?account=${ changeOwnershipItem.accountId }`;
    }
    history.push(redirectUrl);
  }

  closeFromOwnershipModal() {
    const { dispatch } = this.props;
    dispatch(hideOwnershipModal());
  }

  render() {
    const {
      reportingResponse,
      isExportingToExcel,
      isOwnershipModalShown,
      changeOwnershipItem,
      dateFormat,
    } = this.props;
    const {
      gridCriteria,
      loading,
      initialLoaded,
      searchCriteria
    } = this.state;
    const {
      tabs,
      activeTab
    } = this.state;
    const isNoData = (!reportingResponse || !reportingResponse.results || !reportingResponse.results.length);

    return (
      <div className="container validation-results-panel">
        <div className="panel no-border">
          <nav className="panel-nav">
            <ul className="tab">
              { tabs != null && tabs.map(tab => (
                <li
                  key={ tab.index }
                  className={ `tab-item ${ tab.index === activeTab ? 'active' : '' }` }
                >
                  <button
                    type="button"
                    tabIndex={ tab.index }
                    onClick={ () => this.selectTab(tab.index) }
                  >{ tab.title }</button>
                </li>
              )) }
            </ul>

            <aside className="tool-panel">
              <div>
                <DatePicker
                  selected={ searchCriteria.dateFrom }
                  fixedHeight
                  showMonthDropdown
                  placeholderText="Date from"
                  popperPlacement="bottom-start"
                  popperModifiers={ {
                    preventOverflow: {
                      enabled: true,
                      escapeWithReference: true,
                      boundariesElement: 'viewport'
                    }
                  } }
                  autoComplete="off"
                  isClearable
                  maxDate={ searchCriteria.dateTo ?? new Date() }
                  showYearDropdown
                  dropdownMode="select"
                  className="form-input"
                  dateFormat={ dateFormat }
                  onChange={ e => this.dateFromInGridEvent(e) }
                />
              </div>
              <div>
                <DatePicker
                  selected={ searchCriteria.dateTo }
                  fixedHeight
                  showMonthDropdown
                  placeholderText="Date to"
                  popperPlacement="bottom-start"
                  popperModifiers={ {
                    preventOverflow: {
                      enabled: true,
                      escapeWithReference: true,
                      boundariesElement: 'viewport',
                    }
                  } }
                  autoComplete="off"
                  isClearable
                  minDate={ searchCriteria.dateFrom }
                  maxDate={ new Date() }
                  showYearDropdown
                  dropdownMode="select"
                  className="form-input"
                  dateFormat={ dateFormat }
                  onChange={ e => this.dateToInGridEvent(e) }
                />
              </div>
              <div className="input-group">
                <input
                  type="search"
                  className="form-input"
                  placeholder="Search..."
                  onKeyUp={ e => this.searchInGridEvent(e) }
                  onChange={ e => this.searchChanged(e) }
                />
                <span className="input-group-addon">
                  <span className="icon icon-search" />
                </span>
              </div>
              <div className="search-dropdown dropdown dropdown-right">
                <button type="button" className="btn dropdown-toggle" title="Search">
                  <span className="icon icon-search" />
                </button>
                <ul className="menu">
                  <input
                    type="search"
                    className="form-input"
                    placeholder="Search..."
                    disabled={ isNoData }
                    onKeyUp={ e => this.searchInGridEvent(e) }
                  />
                </ul>
              </div>
              <AccessControl allowedPermissions={ [ActivityPerms.miDownload] }>
                <button
                  type="button"
                  title="Generate report"
                  className="btn"
                  onClick={ () => this.downloadExcel() }
                  disabled={ isNoData || isExportingToExcel }
                >
                  <span className={ `icon ${ isExportingToExcel ? 'loading' : 'icon-share' } mr` } />
                  <span>Generate report</span>
                </button>
              </AccessControl>

            </aside>
          </nav>
          <div className="panel-body bordered">
            <ReactTable
              data={ reportingResponse.results ?? [] }
              gridCriteria={ gridCriteria }
              onGridCriteriaChanged={ this.onGridCriteriaChanged }
              actions={ this.getActions() }
              enableExpandCollapse={ false }
              enableSelection={ false }
              loading={ loading }
              initialLoaded={ initialLoaded }
            />
          </div>
        </div>
        { changeOwnershipItem != null && <ChangeOwnershipModal
          isShown={ isOwnershipModalShown }
          onAssign={ () => this.reAssignItem() }
          onContinueWithoutAssign={ () => this.continueFromOwnershipModal() }
          onCancel={ () => this.closeFromOwnershipModal() }
          workflowRecord={ changeOwnershipItem }
        /> }
      </div>
    );
  }
}

function mapStateToProps(state) {
  const {
    auth: {
      permissions,
      settings,
      piiValidationCodes
    },
    forms: {
      isGettingReporting,
      reportingResponse,
      isExportingToExcel,
      gridTotalLimit
    },
    workflow: {
      isOwnershipModalShown,
      changeOwnershipItem
    }
  } = state;
  return {
    permissions,
    settings,
    gridTotalLimit,
    isGettingReporting,
    reportingResponse,
    isExportingToExcel,
    isOwnershipModalShown,
    changeOwnershipItem,
    piiValidationCodes
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(ValidationResults, 'ValidationResults'));
